import { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation, Outlet, Navigate } from "react-router-dom";
import { useStable } from "../context/StableContext";
import { Check, Loader2 } from "lucide-react";
import { useNavigation } from "../hooks/useNavigation";
import { routeTitle } from "../config/navigationConfig";
import DesktopSidebar from "./DesktopSidebar";
import TopBar from "./TopBar";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Button } from "./ui/button";
import FeedbackDialog from "./FeedbackDialog";
import ReleaseNotesGate from "./ReleaseNotesGate";

const ROLE_LABEL = { admin: "Gestore", manager: "Gestore", staff: "Staff", owner: "Proprietario cavallo", readonly: "Lettura" };
const STATUS_LABEL = { active: "attiva", suspended: "sospesa", deactivated: "disattivata", deleted: "eliminata" };
const fmtIT = (d) => { if (!d) return "—"; const [y, m, dd] = String(d).slice(0, 10).split("-"); return (y && m && dd) ? `${dd}/${m}/${y}` : String(d); };

const SIDEBAR_KEY = "equiro_sidebar_mode";
const SIDEBAR_MODE_VALUES = ["open", "closed", "hover"];

export default function AppLayout() {
  const { stables, current, select, loading, assist, exitAssist } = useStable();
  const { mainGroups, utilityItems } = useNavigation();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [newsOpen, setNewsOpen] = useState(false);
  const [sidebarMode, setSidebarMode] = useState(() => {
    try {
      const v = localStorage.getItem(SIDEBAR_KEY);
      if (SIDEBAR_MODE_VALUES.includes(v)) return v;
      // migrate legacy boolean flag
      return localStorage.getItem("equiro_sidebar_collapsed") === "1" ? "closed" : "open";
    } catch { return "open"; }
  });
  const setMode = (m) => {
    setSidebarMode(m);
    try { localStorage.setItem(SIDEBAR_KEY, m); } catch { /* noop */ }
  };
  const go = (p) => { navigate(p); setDrawerOpen(false); };
  const location = useLocation();
  const [pageTitle, setPageTitle] = useState(null);
  useEffect(() => { setPageTitle(null); }, [location.pathname]);
  const headerTitle = pageTitle || routeTitle(location.pathname);

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF8F5]">
        <Loader2 className="h-8 w-8 animate-spin text-[#1B3B2B]" />
      </div>
    );
  if (!current) return <Navigate to="/scuderie" replace />;

  const lic = current.license;
  let licBanner = null;
  if (!assist && lic) {
    if (lic.status === "suspended") licBanner = "La licenza di questa scuderia è sospesa: le operazioni sono bloccate. Contatta il Platform Admin.";
    else if (lic.status === "expired") licBanner = "Il periodo di utilizzo di questa scuderia è terminato: sei in sola lettura. I tuoi dati sono conservati; contatta il Platform Admin per attivare una licenza e riprendere.";
    else if (lic.status === "trial" && lic.expires_at) {
      const days = Math.ceil((new Date(String(lic.expires_at).slice(0, 10) + "T00:00:00") - new Date(new Date().toISOString().slice(0, 10) + "T00:00:00")) / 86400000);
      licBanner = days <= 0
        ? "Il periodo di prova (Trial) è terminato: sei in sola lettura. I dati sono conservati; contatta il Platform Admin per attivare una licenza."
        : `Sei in prova (Trial): restano ${days} ${days === 1 ? "giorno" : "giorni"} (fino al ${fmtIT(lic.expires_at)}).`;
    }
    else if (lic.expires_at) {
      const days = Math.ceil((new Date(String(lic.expires_at).slice(0, 10) + "T00:00:00") - new Date(new Date().toISOString().slice(0, 10) + "T00:00:00")) / 86400000);
      if (days >= 0 && days <= 7) licBanner = `La licenza di questa scuderia scade tra ${days} ${days === 1 ? "giorno" : "giorni"} (${fmtIT(lic.expires_at)}). Contatta il Platform Admin per il rinnovo.`;
      else if (days > 7 && days <= 30) licBanner = `La licenza di questa scuderia scade il ${fmtIT(lic.expires_at)}. Contatta il Platform Admin per il rinnovo.`;
    }
  }

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      {/* Unified full-width top bar (mobile + desktop). Owns the Equiro logo on desktop. */}
      <TopBar
        onOpenDrawer={() => setDrawerOpen(true)}
        onOpenFeedback={() => setFeedbackOpen(true)}
        onOpenNews={() => setNewsOpen(true)}
        headerTitle={headerTitle}
        sidebarMode={sidebarMode}
      />

      <div className="lg:flex">
        {/* Desktop layout spacer: reserves rail/expanded width. In "hover" mode the rail stays
            68px and the hover-expansion overlays on top (no layout shift). */}
        <div aria-hidden
          className={`hidden lg:block shrink-0 transition-[width] duration-200 ${sidebarMode === "open" ? "w-60" : "w-[68px]"}`} />

        {/* Desktop sidebar: starts below the top bar, dedicated to navigation only. */}
        <DesktopSidebar mode={sidebarMode} onSetMode={setMode} />

        <div className="flex-1 min-w-0 flex flex-col min-h-[calc(100vh-4rem)]">
        {assist && (
          <div data-testid="assist-banner"
            className="bg-[#1B3B2B] text-[#FAF8F5] text-sm px-4 py-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-center">
            <span><strong>Modalità assistenza Platform Admin — sola lettura.</strong> Stai consultando “{current.name}”. Nessuna modifica è consentita.</span>
            <Button size="sm" data-testid="exit-assist-btn"
              onClick={async () => { await exitAssist(); navigate("/platform-admin"); }}
              className="h-7 bg-[#C9A227] hover:bg-[#b8941f] text-[#1B3B2B]">Torna alla Console</Button>
          </div>
        )}
        {licBanner && (
          <div data-testid="license-expiry-banner"
            className="bg-amber-100 border-b border-amber-300 text-amber-900 text-sm px-4 py-2 text-center">
            {licBanner}
          </div>
        )}
        {current.status && current.status !== "active" && (
          <div data-testid="stable-status-banner"
            className="bg-amber-100 border-b border-amber-300 text-amber-900 text-sm px-4 py-2 text-center">
            Questa scuderia è <strong>{STATUS_LABEL[current.status]}</strong>. Le operazioni sono limitate; per assistenza contatta il Platform Admin.
          </div>
        )}

        <main className="max-w-7xl mx-auto w-full px-4 sm:px-6 py-6 sm:py-8 pb-28 lg:pb-8">
          <Outlet context={{ setPageTitle }} />
        </main>
        </div>
      </div>

      {/* Mobile hamburger drawer — same navigation model as the desktop sidebar */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="left" className="w-[300px] p-0 pt-safe pl-safe flex flex-col" data-testid="mobile-drawer">
          <SheetHeader className="p-4 border-b border-[#E5DEC9] text-left">
            <div className="flex items-center gap-2">
              <img src="/icon-192.png" alt="Equiro" className="h-9 w-9 rounded-xl object-cover" />
              <SheetTitle className="font-display font-extrabold text-xl text-[#1B3B2B]">Equiro</SheetTitle>
            </div>
            <SheetDescription className="sr-only">Menu di navigazione dei moduli della scuderia</SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto p-3 space-y-1">
            <p className="font-mono-label text-[10px] text-[#8C532B] px-3 pt-1 pb-1">Scuderia</p>
            {stables.map((s) => (
              <button key={s.id} data-testid={`drawer-stable-${s.id}`} onClick={() => { select(s); setDrawerOpen(false); }}
                className="w-full flex items-center justify-between rounded-lg px-3 py-2 text-left hover:bg-[#F0EAE1] transition-colors">
                <span className="min-w-0">
                  <span className="block font-medium text-sm text-[#1B3B2B] truncate">{s.name}</span>
                  <span className="block text-xs text-[#5F6B64]">{ROLE_LABEL[s.role]}</span>
                </span>
                {s.id === current.id && <Check className="h-4 w-4 text-[#8C532B] shrink-0" />}
              </button>
            ))}
            <button data-testid="drawer-manage-stables" onClick={() => go("/scuderie")}
              className="w-full text-left rounded-lg px-3 py-2 text-sm font-semibold text-[#8C532B] hover:bg-[#F0EAE1] transition-colors">
              Gestisci scuderie…
            </button>

            <div className="border-t border-[#E5DEC9] my-2" />
            {mainGroups.map((g) => (
              <div key={g.key}>
                <p data-testid={`drawer-group-${g.key}`} className="font-mono-label text-[10px] text-[#9AA39C] px-3 pt-2 pb-1">{g.label}</p>
                {g.items.map((n) => (
                  <DrawerLink key={n.key} icon={n.icon} label={n.label} onClick={() => go(n.to)} testid={`drawer-${n.key}`} />
                ))}
              </div>
            ))}

            {utilityItems.length > 0 && (
              <>
                <div className="border-t border-[#E5DEC9] my-2" />
                {utilityItems.map((n) => (
                  <DrawerLink key={n.key} icon={n.icon} label={n.label} onClick={() => go(n.to)} testid={`drawer-${n.key}`} />
                ))}
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Mobile bottom navigation */}
      <MobileTabBar />

      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
      <ReleaseNotesGate historyOpen={newsOpen} setHistoryOpen={setNewsOpen} />
    </div>
  );
}

function DrawerLink({ icon: Icon, label, onClick, testid, className = "" }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`w-full flex items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-[#1B3B2B] hover:bg-[#F0EAE1] transition-colors ${className}`}>
      {Icon && <Icon className="h-4 w-4" />} {label}
    </button>
  );
}

function MobileTabBar() {
  const { tabItems } = useNavigation();
  return (
    <nav data-testid="mobile-tabbar"
      className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-[#E5DEC9] flex items-stretch justify-around pb-safe px-safe">
      {tabItems.map((t) => {
        const label = t.tabLabel || t.label;
        return (
          <NavLink key={t.key} to={t.to} end={t.end} data-testid={`tab-${label.toLowerCase()}`}
            className={({ isActive }) =>
              "flex-1 min-w-0 flex flex-col items-center justify-center gap-0.5 py-2.5 px-0.5 text-[10px] leading-tight font-medium whitespace-nowrap transition-colors " +
              (isActive ? "text-[#1B3B2B]" : "text-[#9AA39C]")}>
            <t.icon className="h-5 w-5 shrink-0" />
            <span className="w-full text-center truncate">{label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}
